/**
 * @fileoverview Case Ingestion Assist Panel - Content-only panel body.
 *
 * Shows imported values, unresolved gaps, and source notes for the current step.
 * The sidebar wrapper (AssistSidebarPanel) handles visibility and chrome.
 *
 * Reading order: unresolved items first, then imported values.
 */
import React from 'react';

import { Badge } from '@/components/ui/badge';

import {
  AllFieldsOverview,
  ConsolidatedNotesSection,
  ImportMetadata,
  StepContent,
  StepNotesSection,
} from './AssistFieldRows';
import { SourceUrlsBar } from './SourceUrlsBar';
import type { CaseIngestionAssistState } from './types';

interface CaseIngestionAssistPanelProps {
  assistState: CaseIngestionAssistState;
  /** Current step ID - source notes only show on case_summary step */
  currentStepId?: string;
  /** Callback to snap the browser agent viewer to a specific URL */
  onSnapToUrl?: (url: string) => void;
}

export const CaseIngestionAssistPanel: React.FC<CaseIngestionAssistPanelProps> = ({
  assistState,
  currentStepId,
  onSnapToUrl,
}) => {
  const {
    hasImport,
    importedAt,
    sourceNotes,
    currentStepSummary,
  } = assistState;

  // No import — show nothing
  if (!hasImport) return null;

  const notesRow = currentStepSummary?.rows.find(
    (r) => r.fieldId.endsWith('_notes') && typeof r.importedValue === 'string',
  );

  return (
    <div className="space-y-3">
      {/* Step-level unresolved badge */}
      {currentStepSummary && currentStepSummary.unresolvedCount > 0 && (
        <Badge variant="secondary" className="h-5 px-2 text-[10px] font-medium">
          {currentStepSummary.unresolvedCount} to review
        </Badge>
      )}

      {/* Source URLs for this step */}
      {currentStepSummary && currentStepSummary.sourceUrls.length > 0 && (
        <SourceUrlsBar urls={currentStepSummary.sourceUrls} onSnapToUrl={onSnapToUrl} />
      )}

      {/* Field rows */}
      {currentStepSummary ? (
        <StepContent summary={currentStepSummary} />
      ) : (
        <p className="text-xs font-medium text-zinc-500">
          No imported fields for this step.
        </p>
      )}

      {/* Step-level notes (liability, treatment, coverage) — shown prominently */}
      {notesRow && currentStepId !== 'case_summary' && (
        <StepNotesSection row={notesRow} />
      )}

      {/* Consolidated notes — only on the Notes (case_summary) step */}
      {currentStepId === 'case_summary' && (
        <ConsolidatedNotesSection
          stepSummaries={assistState.stepSummaries}
          sourceNotes={sourceNotes}
        />
      )}

      {/* All imported fields overview — only on the Notes (case_summary) step */}
      {currentStepId === 'case_summary' && (
        <AllFieldsOverview stepSummaries={assistState.stepSummaries} />
      )}

      {/* Import metadata */}
      <ImportMetadata importedAt={importedAt} />
    </div>
  );
};
